import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import { Bubble } from 'tea-component';
import { getOpinionLiveVenue } from '@/pages/opinion/services';
import type { SingleCommonType } from '../../types';
import { debounce } from 'lodash-es';
import { commonBackInfo, uniqueArray } from '../../utils';
import { useIntl } from 'umi';
import { useSetSelectDefaultValue } from '../../hooks/init';
import ChannelLabel from '../../../ChannelLabel';
import CommonAllSelect from '../../components/CommonAllSelect';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';

const linkFlag = '_#_';
const LiveVenuesSelect = ({
  gameInfo,
  onChange,
  defaultValue,
  defaultKey,
  filterValue,
  collectionCallback,
}: SingleCommonType) => {
  const [keyword, setKeyword] = useState<string>('');
  const [basicOptions, setBasicOptions] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const filterParams = useRef<any>({});

  useEffect(() => {
    collectionCallback?.(
      'live_venues',
      (value: any) => {
        Object.assign(filterParams.current, value);
      },
      ['date', 'monitor'],
    );
  }, []);

  const {
    selects: values,
    setSelects: setValues,
    isFirst,
    diffClassName,
  } = useSetSelectDefaultValue<string>({
    defaultValue,
    filterValue,
    defaultKey: 'live_venues',
    options: [],
  });
  const cancelFunc = useRef<any>(null);
  const getLiveVenuesFilter = () => {
    if (gameInfo?.edition_unified_id) {
      loading === false && setLoading(true);
      if (cancelFunc.current !== null) {
        cancelFunc.current();
        cancelFunc.current = null;
        setTimeout(() => {
          setLoading(true);
        }, 1);
      }

      let { social_media_by_keyword, ...otherParams } =
        filterParams.current || {};
      getOpinionLiveVenue(
        {
          ...gameInfo,
          ...otherParams,
          content: keyword,
          top_n: 30,
        },
        (cancel) => {
          cancelFunc.current = cancel;
        },
      )
        .then((data: any) => {
          setBasicOptions(data.live_list || []);
        })
        .catch(() => {
          setBasicOptions([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const debounceGetLiveVenuesFilter = debounce(() => {
    getLiveVenuesFilter();
  }, 500);

  useEffect(() => {
    if (!isFirst) {
      debounceGetLiveVenuesFilter();
    }
  }, [gameInfo, keyword]);

  function getItemElement(liveVenues: {
    channel_name: any;
    live_title: string;
  }) {
    return (
      <span className="dbo-live-text-info">
        <ChannelLabel
          showText={false}
          iconStyle={{ width: 16, height: 16, marginRight: 4 }}
          channel_name={liveVenues.channel_name}
        ></ChannelLabel>
        {liveVenues.live_title}
      </span>
    );
  }

  const options = useMemo(() => {
    let basicLiveVenues = basicOptions || [];

    return basicLiveVenues.map((liveVenues: any) => {
      return {
        text: (
          <Bubble
            closeOnScroll
            dark
            placement="top"
            content={liveVenues.live_title}
          >
            {getItemElement(liveVenues)}
          </Bubble>
        ),
        value: liveVenues.channel_name + linkFlag + liveVenues.live_title,
      };
    });
  }, [basicOptions]);

  const getButtonElement = () => {
    const len = values.length;
    if (len === 0) {
      return formatMessage({ id: 'All' });
    }
    if (len === 1) {
      const item = values[0];
      const [channel_name, live_title] = item.split(linkFlag);
      return getItemElement({ channel_name, live_title });
    }
    return len;
  };

  useEffect(() => {
    const text = getButtonElement();

    onChange?.(
      'live_venues',
      commonBackInfo({ live_venues: values }, { [defaultKey]: text }),
    );
  }, [values, formatMessage]);

  const changeAllSelected = (isAll: boolean) => {
    const allOptionsValue = options.map((item) => item.value);
    if (isAll) {
      setValues(values.filter((item: any) => !allOptionsValue.includes(item)));
    } else {
      setValues(uniqueArray([...values, ...allOptionsValue]));
    }
  };

  return (
    <div className={`dbo-live-venues-select ${diffClassName}`}>
      <CommonAllSelect
        options={loading ? [] : options}
        value={values}
        keyword={keyword}
        leftText="Stream Sessions"
        boxStyle={{ maxHeight: 398, width: 320 }}
        className="dbo-live-venues-select-input"
        boxClassName="dbo-live-venues-select-box"
        placeholderKey="liveVenues"
        searchPlaceholder={'Search'}
        loading={loading}
        pointKey={FilterButtonPoint.liveVenues}
        // allRadioTips={reviewerFilterTips}
        allRadioClick={() => setValues([])}
        loadingText={
          'Under the current selected time frame and official account, there is no data for the corresponding stream sessions. You may need to change other filtering conditions.'
        }
        onOpenChange={(value: boolean) => {
          if (value) {
            if (values.length === 0) {
              getLiveVenuesFilter();
            } else {
              const filterValue = values
                .map((item: string) => {
                  const [channel, title] = item?.split(linkFlag) || [];
                  if (channel) {
                    return {
                      live_title: title,
                      channel_name: channel,
                    };
                  }
                })
                .filter(Boolean);
              setBasicOptions(filterValue);
            }
          } else {
            setBasicOptions([]);
            setKeyword('');
          }
        }}
        changeValues={(values: any) => {
          setValues(values);
          if (values.length === 0) {
            getLiveVenuesFilter();
          }
        }}
        onSearch={getLiveVenuesFilter}
        changeAllSelected={changeAllSelected}
        getButtonElement={getButtonElement}
        onSearchValueChange={(value: string) => setKeyword(value)}
      ></CommonAllSelect>
    </div>
  );
};

export default LiveVenuesSelect;
